import { css } from "@emotion/css";
import { SectionHeader, LegacyDataTable, LegacyDataTableBody, LegacyDataTableRow, LegacyDataTableRowColumn, LegacyDataTableRowHeaderColumn } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery, useMutation } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDGatewayResource, Repository } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { Moment } from "moment";
import moment from "moment/moment";
import { useMemo, useState } from "react";
import semver from "semver/preload";
import { ArgoCDGatewayHealthCallout } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDGatewayHealthCallout";
import { KubernetesPodLogs } from "~/components/KubernetesPodLogsTable/KubernetesPodLogs";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { createErrorInfoFromError } from "~/components/PageContent/createErrorInfo";
import type { ArgoCDInstancePageLoaderData } from "./ArgoCDInstancePageLoaderData";
const styles = {
    alignLeft: css({
        "&&": {
            textAlign: "left",
            paddingLeft: space[16],
        },
    }),
    tableWrapper: css({
        paddingTop: space[16],
    }),
};
type ArgoCDInstanceConnectivityPageProps = {
    loaderData: ArgoCDInstanceConnectivityPageLoaderData;
};
export type ArgoCDInstanceConnectivityPageLoaderData = ArgoCDInstancePageLoaderData;
export async function argoCDInstanceConnectivityPageLoader(_: Repository, gatewayPromise: Promise<ArgoCDGatewayResource>): Promise<ArgoCDInstanceConnectivityPageLoaderData> {
    const gateway = await gatewayPromise;
    return { gateway };
}
export const ArgoCDInstanceConnectivityPage = ({ loaderData }: ArgoCDInstanceConnectivityPageProps) => {
    const { gateway } = loaderData;
    const { navigate } = useSpaceAwareNavigation();
    const [requestCompletedTime, setRequestCompletedTime] = useState<Moment>(moment());
    const [showPreviousContainer, setShowPreviousContainer] = useState<boolean>(false);
    const canViewPreviousContainerLogs = useMemo(() => {
        // If we don't have a version or the version is for local running then just return true
        if (!gateway.InstallDetails?.GatewayVersion || gateway.InstallDetails?.GatewayVersion === "0.0.1-local") {
            return true;
        }
        const gatewayVersion = semver.parse(gateway.InstallDetails?.GatewayVersion);
        // v1.12.0 is the first version of the gateway that allows viewing previous container logs of the gateway
        const requiredVersion = semver.parse("1.12.0");
        if (!requiredVersion || !gatewayVersion) {
            return false;
        }
        return semver.gte(gatewayVersion, requiredVersion);
    }, [gateway]);
    const { result: podLogs, isLoading: isLoadingLogs, error: logsError, refetch: refreshLogs, } = useInlineStatusQuery(async (repo) => {
        const logLines = await repo.ArgoCDGateways.getPodLogs(gateway.Id, { ShowPreviousContainer: showPreviousContainer });
        setRequestCompletedTime(moment());
        return logLines;
    }, [gateway, showPreviousContainer], "Load Argo CD Gateway pod logs");
    const { execute: createHealthCheckTask, isExecuting: isCreatingHealthCheckTask } = useMutation({
        name: "Create ArgoCDGatewayHealthCheck task",
        action: (repo) => repo.Tasks.createHealthCheckTaskForArgoCDGateway(gateway),
        afterComplete: async (_, createdTask) => {
            navigate(links.taskPage.generateUrl({ taskId: createdTask.Id }));
        },
    });
    const dataRows = useMemo(() => {
        if (!gateway.InstallDetails) {
            return [];
        }
        return [
            ["Gateway Version", gateway.InstallDetails.GatewayVersion],
            ["Helm Chart Version", gateway.InstallDetails.HelmChartVersion],
            ["Helm Release Name", gateway.InstallDetails.HelmReleaseName],
            ["Namespace", gateway.InstallDetails.KubernetesNamespace],
            ["Argo CD Version", gateway.InstallDetails.ArgoCDVersion ?? "Unknown"],
        ];
    }, [gateway]);
    const logsErrorInfo = useMemo(() => logsError && createErrorInfoFromError(logsError), [logsError]);
    return (<PageContent header={{
            title: "Gateway Connectivity",
            primaryAction: {
                type: "button",
                label: "Check health",
                onClick: createHealthCheckTask,
                disabled: isCreatingHealthCheckTask,
            },
        }}>
            <ArgoCDGatewayHealthCallout gateway={gateway}/>
            {gateway.InstallDetails && (<section>
                    <SectionHeader title={"Details"}/>
                    <LegacyDataTable>
                        <LegacyDataTableBody>
                            {dataRows.map(([header, value]) => (<LegacyDataTableRow key={header}>
                                    <LegacyDataTableRowHeaderColumn className={styles.alignLeft}>{header}</LegacyDataTableRowHeaderColumn>
                                    <LegacyDataTableRowColumn>{value}</LegacyDataTableRowColumn>
                                </LegacyDataTableRow>))}
                        </LegacyDataTableBody>
                    </LegacyDataTable>
                </section>)}
            <section>
                <SectionHeader title={"Gateway Pod Logs"}/>
                <div className={styles.tableWrapper}>
                    <KubernetesPodLogs logLines={podLogs} isLoading={isLoadingLogs} errorInfo={logsErrorInfo} localStoragePrefix={"Octopus.Infrastructure.ArgoCDInstances.GatewayConnectivity"} lastRefreshed={requestCompletedTime} refresh={refreshLogs} backgroundColor={"primary"} onPreviousContainerChanged={canViewPreviousContainerLogs ? (enabled) => setShowPreviousContainer(enabled) : undefined}/>
                </div>
            </section>
        </PageContent>);
};
